import request from "../../utils/request";
import { Result } from "../../models/general";
import type { ReportExportLabels } from "../../components/reports/reportExportRequest";

/**
 * Shared filter for /payroll-reports. fromDate/toDate are MONTH selectors (any day inside the
 * month works; the server snaps to whole months and bounds the end month exclusively).
 * `currencyId` / `pageNumber` / `pageSize` were removed: the service never read them.
 */
export interface PayrollReportInput {
    fromDate: string;
    toDate: string;
    departmentIds?: number[];
}

/** One row per (department, month, currency). Never sum across currencies. */
export interface PayrollSummaryView {
    departmentName: string;
    departmentId: number;
    month: string;
    currencyCode: string;
    grossTotal: number;
    deductionsTotal: number;
    netTotal: number;
    /** Money (OvertimePay). Payslips that only carry legacy OT hours contribute 0 — see legacyOvertimeRows. */
    overtimeTotal: number;
    legacyOvertimeRows: number;
    variablePayTotal: number;
    headcount: number;
}

/** One row per payslip (employee × month × currency). */
export interface SalaryRegisterView {
    employeeId: string;
    employeeName: string;
    departmentName: string;
    month: string;
    grossSalary: number;
    deductions: number;
    variablePay: number;
    /** Back-compat alias of overtimePay (money). */
    overtime: number;
    overtimePay: number | null;
    overtimeHours: number;
    adjustments: number;
    netPay: number;
    currencyCode: string;
}

/** One row per (department, month, currency). totalCost = cost to company, not net. */
export interface DepartmentCostView {
    departmentName: string;
    departmentId: number;
    month: string;
    currencyCode: string;
    grossTotal: number;
    additionalEarnings: number;
    employerContributions: number;
    totalCost: number;
    netTotal: number;
    headcount: number;
    /** Back-compat alias of avgGrossSalary. */
    avgSalary: number;
    avgGrossSalary: number;
    avgCostPerEmployee: number;
}

/** Attendance & Overtime Details — one row per (employee, month). */
export interface AttendanceOvertimeView {
    employeeId: string;
    employeeName: string;
    departmentName: string;
    month: string;
    scheduledDays: number;
    presentDays: number;
    /**
     * O4 — days WORKED with no schedule at all (per-day status NonWorkingDay/Holiday + worked hours),
     * counted as present but kept out of `presentDays` so `scheduledDays >= presentDays + absentDays`
     * still holds. Rendered as its own "Unscheduled Present" column.
     */
    unscheduledPresentDays: number;
    absentDays: number;
    leaveDays: number;
    lateArrivals: number;
    lateMinutes: number;
    earlyLeaves: number;
    earlyLeaveMinutes: number;
    totalWorkedHours: number;
    regularOvertimeHours: number;
    premiumOvertimeHours: number;
    overtimePay: number | null;
    currencyCode: string;
    overtimeSource: 'Payroll' | 'Attendance' | string;
    hasPayrollRow: boolean;
}

export async function getPayrollSummary(input: PayrollReportInput): Promise<Result<PayrollSummaryView[]>> {
    const response = await request.post<Result<PayrollSummaryView[]>>("PayrollReports/GetPayrollSummary", input);
    return response.data;
}

export async function getSalaryRegister(input: PayrollReportInput): Promise<Result<SalaryRegisterView[]>> {
    const response = await request.post<Result<SalaryRegisterView[]>>("PayrollReports/GetSalaryRegister", input);
    return response.data;
}

export async function getDepartmentCost(input: PayrollReportInput): Promise<Result<DepartmentCostView[]>> {
    const response = await request.post<Result<DepartmentCostView[]>>("PayrollReports/GetDepartmentCost", input);
    return response.data;
}

export async function getAttendanceOvertime(input: PayrollReportInput): Promise<Result<AttendanceOvertimeView[]>> {
    const response = await request.post<Result<AttendanceOvertimeView[]>>("PayrollReports/GetAttendanceOvertime", input);
    return response.data;
}

/* ── Attendance & Overtime, per day (SPEC docs/reports-revamp/SPEC.md §3) ───────────────────── */

/**
 * The day's shape in the report. `Unscheduled` is a WeekOff/Holiday that nevertheless carries worked
 * hours (the O4 rule), and `Future` is a day inside the period that has not happened yet — it is not
 * an absence and carries zeros. Tone and label come from `components/reports/StatusChip`.
 */
export type AttendanceDayStatus =
    | 'Present' | 'Late' | 'Absent' | 'Leave' | 'HalfDayLeave' | 'SickLeave'
    | 'Holiday' | 'WeekOff' | 'Unscheduled' | 'Future';

/** `Mixed` = the day's shifts disagree; `NotRequired` = the CO-304 approval gate is not armed. */
export type OvertimeApprovalStatus = 'NotRequired' | 'Pending' | 'Approved' | 'Rejected' | 'Mixed';

/** One calendar day of an employee-month. */
export interface AttendanceOvertimeDayView {
    /** ISO datetime at the employee's LOCAL midnight. Read as a calendar date, never via `new Date()`. */
    date: string;
    dayStatus: AttendanceDayStatus | string;
    scheduledHours: number;
    workedHours: number;
    /** Local `HH:mm`, or null when the day has no ended shift. */
    clockIn: string | null;
    clockOut: string | null;
    /** PAYABLE hours — the same carriers payroll reads, after the approval gate. */
    regularOvertimeHours: number;
    premiumOvertimeHours: number;
    /** Display-only: worked, decided against, never paid. */
    rejectedOvertimeHours: number;
    /** Withheld by the approval gate. 0 when the gate is not armed. */
    pendingApprovalHours: number;
    overtimeApprovalStatus: OvertimeApprovalStatus | string;
    /** Rate-based ESTIMATE of what this day's payable overtime is worth. */
    overtimeCost: number;
    lateMinutes: number;
    earlyLeaveMinutes: number;
}

/** One employee-month, with every calendar day of the period inside it. */
export interface AttendanceOvertimeDetailView {
    employeeId: string;
    employeeName: string;
    departmentName: string;
    /** `yyyy-MM`. */
    month: string;
    currencyCode: string;
    hasPayrollRow: boolean;
    overtimeSource: 'Payroll' | 'Attendance' | string;
    /** Null when no gross-pay components resolve for the month; cost is then 0, which is not an error. */
    hourlyRate: number | null;
    regularMultiplier: number;
    premiumMultiplier: number;
    /** `Basic/HoursPerMonth` or `KSA Art.107`. */
    rateBasis: string;
    days: AttendanceOvertimeDayView[];
    scheduledDays: number;
    presentDays: number;
    unscheduledPresentDays: number;
    absentDays: number;
    leaveDays: number;
    lateArrivals: number;
    lateMinutes: number;
    earlyLeaves: number;
    earlyLeaveMinutes: number;
    totalWorkedHours: number;
    regularOvertimeHours: number;
    premiumOvertimeHours: number;
    /** Σ of the days' rate-based cost — an ESTIMATE, not what was paid. */
    overtimeCost: number;
    /** The persisted payslip figure: authoritative for what was PAID. Null when there is no payslip. */
    payslipOvertimePay: number | null;
    payslipRegularOvertimeHours: number | null;
    payslipPremiumOvertimeHours: number | null;
    daysWithOvertime: number;
    pendingApprovalHours: number;
    rejectedOvertimeHours: number;
}

export async function getAttendanceOvertimeDetail(input: PayrollReportInput): Promise<Result<AttendanceOvertimeDetailView[]>> {
    const response = await request.post<Result<AttendanceOvertimeDetailView[]>>("PayrollReports/GetAttendanceOvertimeDetail", input);
    return response.data;
}

/* ── CO-995: the /payroll/table Excel export ─────────────────────────────────────────────────────── */

/** PayrollTableWorkbookText on the server: the already translated words the workbook prints. */
export interface PayrollTableWorkbookText {
    period: string;
    currency: string;
    employees: string;
    employeeCountOne: string;
    employeeCountMany: string;
    paid: string;
    notPaid: string;
    noDepartment: string;
    noPosition: string;
    noCountry: string;
    allEmployees: string;
    groupHeader: string;
    shareOfNetPay: string;
    registerSheet: string;
    summarySheet: string;
    indexSheet: string;
    backToIndex: string;
    note: string;
    notConverted: string;
    notConvertedHeading: string;
    unconvertedRowNote: string;
    unconvertedFootnote: string;
}

export interface PayrollTableExportRequest {
    input: {
        year: number;
        month: number;
        /** The page's currency picker; 0 means the company currency. */
        currencyId: number;
        /** The employees to export. An id outside this company and month gets a 404 and no file. */
        payslipIds: number[];
        layout: 'register' | 'summaryAndRegister' | 'oneSheetPerGroup';
        groupBy: 'none' | 'department' | 'position' | 'country' | 'paymentStatus';
        sortBy: 'name' | 'staffNumber' | 'netPayHighToLow';
        showSubtotals: boolean;
        text: PayrollTableWorkbookText;
    };
    format: 'xlsx';
    culture: string;
    rtl: boolean;
    title: string;
    /** In file order. The server drops unknown keys, forces the name, and drops the labour card outside the UAE. */
    columns: { key: string; header: string }[];
    labels: ReportExportLabels;
}

/**
 * A refused export arrives as a Blob because of `responseType: 'blob'`. The shared interceptor only parses
 * a Blob typed exactly `application/json`, and ASP.NET sends `application/json; charset=utf-8`, so read it
 * here. Otherwise the server's reason never reaches the user and they get the generic failure.
 */
async function readRefusedExport(error: unknown): Promise<unknown> {
    const wrapped = (error as { data?: unknown } | null)?.data;
    const body = error instanceof Blob ? error : wrapped instanceof Blob ? wrapped : null;
    if (!body) return error;
    try {
        return JSON.parse(await body.text());
    } catch {
        return error;
    }
}

export async function DownloadPayrollTableExport(req: PayrollTableExportRequest): Promise<Blob> {
    try {
        const response = await request.post<Blob>("PayrollReports/Export/PayrollTable", req, {
            responseType: 'blob',
            headers: { 'Content-Type': 'application/json' },
        });
        return response.data;
    } catch (error) {
        throw await readRefusedExport(error);
    }
}

/** The per-company rule behind the export's bank name, IBAN and labour card columns. HR only on the server. */
export interface PayrollTableExportSettings {
    allowBankDetails: boolean;
}

export async function GetPayrollTableExportSettings(): Promise<Result<PayrollTableExportSettings>> {
    const response = await request.get<Result<PayrollTableExportSettings>>("PayrollReports/Export/PayrollTable/Settings");
    return response.data;
}

export async function UpdatePayrollTableExportSettings(input: PayrollTableExportSettings): Promise<Result<PayrollTableExportSettings>> {
    const response = await request.put<Result<PayrollTableExportSettings>>("PayrollReports/Export/PayrollTable/Settings", input);
    return response.data;
}
